import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import CtaSection from '@/components/base/CtaSection';
import { testimonials, faqs } from '@/mocks/articles';

export default function About() {
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const formUrl = 'https://readdy.ai/api/form/d96dcov0d76aer3t56g';

  useEffect(() => {
    document.title = '关于我们 - 暗网吃瓜 | 品牌介绍与联系方式';
  }, []);

  const nextTestimonial = () => {
    setTestimonialIndex((i) => (i + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setTestimonialIndex((i) => (i - 1 + testimonials.length) % testimonials.length);
  };

  const currentTestimonial = testimonials[testimonialIndex];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于暗网吃瓜',
    description: '暗网吃瓜 - 网红塌房、明星黑料、博主反差视频首选平台。了解我们的品牌故事、行业声誉与联系方式。',
    url: 'https://anwangchigua.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '暗网吃瓜',
      url: 'https://anwangchigua.com',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://anwangchigua.com' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

        <main>
          {/* Hero */}
          <section className="relative w-full h-[420px] md:h-[520px] flex items-end overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20abstract%20cyber%20texture%20with%20deep%20black%20background%2C%20dark%20red%20radial%20glow%20in%20center%2C%20mysterious%20network%20node%20pattern%2C%20noir%20cinematic%20style%2C%20ominous%20atmosphere%2C%20grainy%20texture%2C%20dramatic%20shadow%2C%20professional%20photography%2C%20no%20text%2C%20no%20people&width=1800&height=900&seq=about-hero-2026&orientation=landscape"
              alt="关于暗网吃瓜"
              title="暗网吃瓜 - 品牌介绍"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent"></div>

            <div className="relative z-10 w-full px-4 md:px-6 pb-14 md:pb-18">
              <div className="max-w-6xl mx-auto">
                <div className="max-w-2xl">
                  <h1 className="font-heading text-3xl md:text-5xl text-foreground-50 leading-tight mb-3 tracking-wider">
                    揭秘暗网吃瓜
                  </h1>
                  <p className="text-foreground-300 text-sm md:text-base max-w-lg">
                    暗网吃瓜 - 网红塌房、明星黑料、博主反差视频首选平台。
                    了解我们的品牌故事、行业声誉与联系方式。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col lg:flex-row items-center gap-10">
                <div className="lg:w-1/2">
                  <div className="relative rounded-3xl overflow-hidden h-[350px] md:h-[450px]">
                    <img
                      src="https://readdy.ai/api/search-image?query=Dark%20mysterious%20detective%20office%20with%20red%20accent%20lighting%2C%20cork%20board%20with%20connected%20strings%20and%20photos%2C%20investigation%20scene%2C%20noir%20cinematic%20style%2C%20professional%20photography%2C%20moody%20atmosphere%2C%20deep%20shadows%2C%20no%20people&width=900&height=700&seq=about-brand-2026&orientation=portrait"
                      alt="暗网吃瓜品牌"
                      title="暗网吃瓜 - 黑料猎人"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-primary-500/5"></div>
                  </div>
                </div>
                <div className="lg:w-1/2">
                  <span className="inline-block px-3 py-1 bg-accent-500/10 text-accent-400 text-xs font-medium rounded-full mb-4">
                    关于我们
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-50 mb-5 tracking-wider">
                    黑料猎人
                  </h2>
                  <div className="space-y-4 text-foreground-400 text-sm leading-relaxed">
                    <p>
                      暗网吃瓜成立于2024年，是中国领先的娱乐八卦资讯聚合平台。
                      我们专注于为吃瓜群众提供最新、最全、最真实的网红塌房、明星黑料和博主反差视频内容。
                    </p>
                    <p>
                      我们的团队由资深娱乐记者、数据分析师和技术工程师组成，通过多源信息交叉验证，
                      确保每一条爆料都经过严格核验。暗网吃瓜网站和暗网吃瓜app同步更新，
                      永久更新入口确保用户不会错过任何重要信息。
                    </p>
                    <p>
                      在短短两年内，暗网吃瓜已成为中国最受欢迎的吃瓜平台之一，
                      日均活跃用户超过百万。我们的使命是让每个人都能第一时间吃到大瓜，吃真瓜，吃好瓜。
                    </p>
                  </div>
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 bg-background-200 text-foreground-200 text-sm rounded-lg hover:bg-background-300 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <span>联系我们</span>
                    <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Reputation - Testimonials */}
          <section id="reputation" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-100 mb-10 tracking-wider">
                （ 行业声誉 ）
              </h2>

              <div className="mb-8">
                <div className="inline-flex items-center gap-1 px-4 py-1.5 bg-background-200 rounded-full mb-6 border border-background-300/30">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                  ))}
                  <span className="text-foreground-200 font-medium text-sm ml-2">
                    {currentTestimonial.rating}.0
                  </span>
                </div>

                <blockquote className="text-foreground-300 text-base md:text-lg leading-relaxed mb-8 font-serif tracking-wide">
                  &ldquo;{currentTestimonial.content}&rdquo;
                </blockquote>

                <div className="flex items-center justify-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-primary-500/30">
                    <img
                      src={currentTestimonial.avatar}
                      alt={currentTestimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-left">
                    <p className="text-foreground-200 text-sm font-medium">{currentTestimonial.name}</p>
                    <p className="text-foreground-500 text-xs">{currentTestimonial.role}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={prevTestimonial}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-foreground-800/30 text-foreground-400 hover:text-foreground-100 hover:border-foreground-400/40 transition-all cursor-pointer"
                >
                  <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                </button>
                <button
                  onClick={nextTestimonial}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-950 hover:bg-primary-600 transition-all cursor-pointer"
                >
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </button>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-50 text-center mb-10 tracking-wider">
                <span className="text-accent-500">「 </span>
                常见问题
                <span className="text-accent-500"> 」</span>
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <details key={index} className="group bg-background-100 rounded-xl border border-background-300/30 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <h3 className="text-foreground-200 text-sm font-medium pr-4">{faq.question}</h3>
                      <i className="ri-add-line text-foreground-500 group-open:hidden w-4 h-4 flex items-center justify-center shrink-0"></i>
                      <i className="ri-subtract-line text-primary-500 hidden group-open:block w-4 h-4 flex items-center justify-center shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-foreground-400 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-50 text-center mb-3 tracking-wider">
                <span className="text-primary-500">「 </span>
                联系我们
                <span className="text-primary-500"> 」</span>
              </h2>
              <p className="text-foreground-500 text-sm text-center mb-10">
                有爆料线索？商务合作？欢迎随时联系我们
              </p>

              <form
                data-readdy-form=""
                action={formUrl}
                method="POST"
                className="space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const honeypot = form.querySelector<HTMLInputElement>('[name="website_alt"]');
                  if (honeypot && honeypot.value.trim() !== '') {
                    const statusEl = form.querySelector('.form-status');
                    if (statusEl) {
                      (statusEl as HTMLElement).textContent = '提交成功！我们会尽快与您联系。';
                      (statusEl as HTMLElement).className = 'form-status text-accent-500 text-sm text-center mt-4';
                    }
                    return;
                  }
                  const formData = new FormData(form);
                  const submitBtn = form.querySelector<HTMLButtonElement>('[type="submit"]');
                  if (submitBtn) {
                    submitBtn.disabled = true;
                    submitBtn.textContent = '提交中...';
                  }
                  fetch(formUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                  })
                    .then(async (res) => {
                      const text = await res.text();
                      let parsed;
                      try { parsed = JSON.parse(text); } catch { parsed = { code: 'Unknown' }; }
                      const statusEl = form.querySelector('.form-status');
                      if (res.ok && parsed.code === 'OK') {
                        if (statusEl) {
                          (statusEl as HTMLElement).textContent = '提交成功！我们会尽快与您联系。';
                          (statusEl as HTMLElement).className = 'form-status text-accent-500 text-sm text-center mt-4';
                        }
                        form.reset();
                      } else {
                        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
                        if (statusEl) {
                          (statusEl as HTMLElement).textContent = serverMsg;
                          (statusEl as HTMLElement).className = 'form-status text-primary-500 text-sm text-center mt-4';
                        }
                      }
                    })
                    .catch(() => {
                      const statusEl = form.querySelector('.form-status');
                      if (statusEl) {
                        (statusEl as HTMLElement).textContent = '网络错误，请稍后重试';
                        (statusEl as HTMLElement).className = 'form-status text-primary-500 text-sm text-center mt-4';
                      }
                    })
                    .finally(() => {
                      if (submitBtn) {
                        submitBtn.disabled = false;
                        submitBtn.textContent = '发送消息';
                      }
                    });
                }}
              >
                <div>
                  <label htmlFor="contact-name" className="block text-foreground-400 text-sm mb-2">
                    您的称呼 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-3 bg-background-200 border border-background-300/40 rounded-xl text-foreground-200 text-sm focus:outline-none focus:border-primary-500/50 transition-colors"
                    placeholder="请输入您的称呼"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-foreground-400 text-sm mb-2">
                    电子邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-3 bg-background-200 border border-background-300/40 rounded-xl text-foreground-200 text-sm focus:outline-none focus:border-primary-500/50 transition-colors"
                    placeholder="请输入您的电子邮箱"
                  />
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-foreground-400 text-sm mb-2">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    className="w-full px-4 py-3 bg-background-200 border border-background-300/40 rounded-xl text-foreground-200 text-sm focus:outline-none focus:border-primary-500/50 transition-colors"
                    placeholder="请选择或输入主题"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-foreground-400 text-sm mb-2">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-background-200 border border-background-300/40 rounded-xl text-foreground-200 text-sm focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-background-950 font-medium text-sm rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer"
                >
                  发送消息
                </button>
                <div className="form-status"></div>
              </form>
            </div>
          </section>

          {/* CTA */}
          <CtaSection
            title="加入百万吃瓜大军"
            subtitle="下载暗网吃瓜app，随时随地吃瓜不迷路"
            buttonText="立即下载暗网吃瓜APP"
          />
        </main>

        <Footer />
      </div>
    </>
  );
}